import { memo, useMemo } from "react";
import { FilledTextArea } from "../../../../components/FilledTextArea";
import {
  TConfigItemType,
  TVerificationAppFieldConfigItem,
} from "../../../app/models";
import { TDocumentData, TDocumentInfo } from "../../models";
import {
  formatConfigFieldDisplayValue,
  resolveConfigFieldLabel,
} from "../../utils/configFieldDisplay";
import { BooleanField } from "./BooleanField";
import { CountrySelectField } from "./CountrySelectField";
import { DateField } from "./DateField";
import { FilePickerField } from "./FilePickerField";
import { SelectField } from "./SelectField";
import { SourceOfActiveField } from "./SourceOfActiveField";
import { TaxIdDisplay } from "./TaxId";
import { TaxIdEditField } from "./TaxIdEditField";
import { UrlField } from "./UrlField";

type Props = {
  field: TVerificationAppFieldConfigItem;
  fields: any;
  allDisabled?: boolean;
  fileItems?: TDocumentData[];
  requestId?: string;
  documentInfo?: TDocumentInfo;
};

export const ConfigContentField = memo((props: Props) => {
  const {
    field,
    fields,
    allDisabled,
    fileItems,
    requestId,
    documentInfo,
  } = props;

  const label = useMemo(() => resolveConfigFieldLabel(field), [field]);

  if (field.type === TConfigItemType.NOTE) {
    return null;
  }

  if (typeof field.name !== "string") {
    return null;
  }

  const namedField = field as TVerificationAppFieldConfigItem & { name: string };

  if (field.type === TConfigItemType.FILE_PICKER) {
    return (
      <FilePickerField
        field={namedField}
        disabled={allDisabled}
        fileItems={fileItems}
        requestId={requestId}
        documentInfo={documentInfo}
      />
    );
  }

  if (field.type === TConfigItemType.DATE) {
    return (
      <DateField
        name={field.name}
        label={label}
        disabled={allDisabled}
      />
    );
  }

  if (field.type === TConfigItemType.TEXT) {
    return (
      <FilledTextArea
        {...(allDisabled ? { value: fields[field.name] } : {})}
        name={field.name}
        label={label}
        inputProps={{ disabled: allDisabled }}
      />
    );
  }

  if (field.type === TConfigItemType.URL) {
    return (
      <UrlField
        name={field.name}
        label={label}
        value={fields[field.name]}
        disabled={allDisabled}
      />
    );
  }

  if (field.type === TConfigItemType.COUNTRY_SELECT) {
    return (
      <CountrySelectField
        name={field.name}
        label={label}
        value={fields[field.name]}
        disabled={allDisabled}
      />
    );
  }

  if (field.type === TConfigItemType.SELECT) {
    return (
      <SelectField
        field={namedField}
        label={label}
        value={fields[field.name]}
        disabled={allDisabled}
      />
    );
  }

  if (field.type === TConfigItemType.SOURCE_OF_ACTIVE) {
    return (
      <SourceOfActiveField
        name={field.name}
        label={label}
        value={fields[field.name]}
        disabled={allDisabled}
      />
    );
  }

  if (field.type === TConfigItemType.TAX_ID) {
    if (!allDisabled) {
      return (
        <TaxIdEditField
          name={field.name}
          label={label}
          multiple={field.multiple}
        />
      );
    }

    return (
      <TaxIdDisplay
        name={field.name}
        fields={fields}
        label={label}
        disabled={allDisabled}
      />
    );
  }

  if (
    field.type === TConfigItemType.CHECKBOX ||
    field.type === TConfigItemType.PEP
  ) {
    return (
      <BooleanField
        name={field.name}
        label={label}
        value={fields[field.name]}
        disabled={allDisabled}
      />
    );
  }

  const displayValue = formatConfigFieldDisplayValue(namedField, fields[field.name]);

  return (
    <FilledTextArea
      {...(allDisabled ? { value: displayValue } : {})}
      name={field.name}
      label={label}
      inputProps={{ disabled: allDisabled }}
    />
  );
});
